// Setup › Books setup › Bring the old books in (gap #17), Blueprint's, on ui-web's parts (BL-189): three
// Sections under the region's heading. The old chart of accounts (imports.chartOfAccounts: an account whose
// code is already here is left as it is); the opening balances at the switch-over date
// (imports.openingBalances), which post only after they tie out; monthly history summaries
// (imports.historySummaries), kept as report comparatives and never posted. Each takes a CSV typed or pasted
// in a TextField, or a file from FileField that fills the same box. What was read waits in a Notice, each
// problem a red line under it (a Notice is never red); what to add is a DataTable. The screen asks for every
// tie-out figure before it sends; the server's tie-out check is a DataTable either way, then what posted, a
// done Notice. Riverstone keeps Imports.tsx. setup-page.css lays it out, tokens only.
import {
  Button,
  DataTable,
  FileField,
  Link,
  Notice,
  SelectField,
  StateMark,
  TextField,
  type DataColumn,
} from '@flowology/ui-web/fl';
import { Section } from '@flowology/ui-web/patterns';
import { useState, type ReactNode } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { docHref } from '../shared/doc-link.ts';
import {
  CHART_TYPES,
  chartRows,
  chartTypeLabel,
  HISTORY_METRICS,
  HISTORY_SOURCES,
  historyRows,
  sourceCode,
  tieCents,
  trialRows,
} from './corrections-model.ts';
import { dayText, money } from './model.ts';
import type { Account } from './types.ts';
import { errorText, useQuery } from './ui.tsx';

export interface TieOut {
  balanced: boolean;
  checks: { what: string; expectedCents: number; importedCents: number; ok: boolean }[];
}

type Toast = (m: string) => void;

const SOURCES = [
  ...HISTORY_SOURCES.map((s) => ({ value: s.code, label: s.label })),
  { value: 'other', label: 'Another system' },
];

/** A CSV typed or pasted in the box, or picked as a file: either fills the same box. */
function CsvBox({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (text: string, fileName?: string) => void;
}) {
  return (
    <div className="bp-setup-csv">
      <TextField label={label} description={hint} multiline value={value} onChange={(t) => onChange(t)} />
      <FileField
        label="Or pick the file"
        accept=".csv,text/csv"
        onChoose={(f) => void f.text().then((t) => onChange(t, f.name))}
      />
    </div>
  );
}

/** What was read, waiting to be sent: a Notice, each problem (the first five) a red line under it. */
function Read({ title, problems }: { title: ReactNode; problems: readonly string[] }) {
  return (
    <div className="bp-setup-read">
      <Notice title={title} />
      {problems.slice(0, 5).map((p) => (
        <p key={p} className="bp-setup-problem">
          <StateMark state="problem" />
          {p}
        </p>
      ))}
    </div>
  );
}

/** Where they came from: a system from the list, or another one by name (the code is made from it). */
function Source({
  picked,
  other,
  onPick,
  onOther,
}: {
  picked: string;
  other: string;
  onPick: (code: string) => void;
  onOther: (name: string) => void;
}) {
  return (
    <>
      <SelectField label="Where they came from" value={picked} onChange={onPick} options={SOURCES} />
      {picked === 'other' && (
        <TextField
          label="The system’s name"
          maxLength={60}
          value={other}
          onChange={onOther}
          error={other.trim() && !sourceCode(other) ? 'The name needs some letters.' : null}
        />
      )}
    </>
  );
}

/** Send, with its -ing words while it goes; why the last try failed under it, red. */
function Send({
  busy,
  busyLabel,
  error,
  ready,
  children,
}: {
  busy: boolean;
  busyLabel: string;
  error: string | null;
  ready: boolean;
  children: string;
}) {
  return (
    <div className="bp-setup-wide bp-setup-send">
      <div className="bp-setup-acts">
        <Button variant="primary" type="submit" isDisabled={!ready || busy}>
          {busy ? busyLabel : children}
        </Button>
      </div>
      {error && (
        <p className="bp-setup-bad" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

const TIE_COLUMNS: DataColumn<TieOut['checks'][number] & { id: string }>[] = [
  { id: 'what', label: 'Check', rowHeader: true, cell: (c) => ({ text: c.what }) },
  {
    id: 'expected',
    label: 'The old books say',
    align: 'end',
    cell: (c) => ({ text: money(c.expectedCents) }),
  },
  { id: 'imported', label: 'This import has', align: 'end', cell: (c) => ({ text: money(c.importedCents) }) },
  {
    id: 'ok',
    label: 'Ties out',
    cell: (c) => ({ word: c.ok ? { word: 'Yes' } : { word: 'No', tone: 'bad' } }),
  },
];

type ChartRow = ReturnType<typeof chartRows>['rows'][number];
const CHART_COLUMNS: DataColumn<ChartRow & { id: string }>[] = [
  { id: 'code', label: 'Code', rowHeader: true, cell: (r) => ({ text: r.code }) },
  { id: 'name', label: 'Name', cell: (r) => ({ text: r.name }) },
  { id: 'type', label: 'Type here', cell: (r) => ({ text: chartTypeLabel(r.subtype) }) },
];

export function SetupImports({
  toast,
  canChart,
  onChart,
}: {
  toast: Toast;
  /** accounting.setup: bring in the old chart of accounts. */
  canChart: boolean;
  /** The chart changed: the page reads it again. */
  onChart: () => Promise<void>;
}) {
  return (
    <div className="bp-setup-imports" role="region" aria-labelledby="imports-title">
      <h2 id="imports-title" className="bp-setup-title">
        Bring the old books in
      </h2>
      {canChart && <ChartImport toast={toast} onChart={onChart} />}
      <OpeningBalances toast={toast} />
      <History toast={toast} />
    </div>
  );
}

function ChartImport({ toast, onChart }: { toast: Toast; onChart: () => Promise<void> }) {
  const [picked, setPicked] = useState<string>(HISTORY_SOURCES[0].code);
  const [other, setOther] = useState('');
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const parsed = chartRows(csv);
  const source = picked === 'other' ? sourceCode(other) : picked;
  const ready = source !== null && parsed.rows.length > 0 && !parsed.problems.length;
  const send = async () => {
    if (!ready || !source) return;
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ created: number; existing: number }>('imports.chartOfAccounts', {
        source,
        rows: parsed.rows,
      });
      const said =
        `Added ${r.created} ${r.created === 1 ? 'account' : 'accounts'} to the chart.` +
        (r.existing
          ? ` ${r.existing} ${r.existing === 1 ? 'was' : 'were'} already here by code and stayed as ${r.existing === 1 ? 'it was' : 'they were'}.`
          : '');
      setDone(said);
      toast(said);
      await onChart();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title="Chart of accounts" level={3}>
      <p className="bp-setup-say">
        The old books’ accounts, before their opening balances: an account whose code is already in the chart
        stays as it is. Rename or archive them after in the chart above.
      </p>
      <form
        className="bp-setup-form"
        aria-label="Bring the old chart in"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy) void send();
        }}
      >
        <Source picked={picked} other={other} onPick={setPicked} onOther={setOther} />
        <CsvBox
          label="Accounts (code, name, type, old id)"
          hint={`One account a line: 6110, Truck fuel, Expense. The type is the old system’s word or ours (${CHART_TYPES.map((t) => t.label).join(', ')}); the old id is optional.`}
          value={csv}
          onChange={(t) => {
            setCsv(t);
            setDone(null);
          }}
        />
        {csv.trim() && (
          <div className="bp-setup-wide bp-setup-read">
            <Read
              title={`${parsed.rows.length} ${parsed.rows.length === 1 ? 'account' : 'accounts'} ready`}
              problems={parsed.problems}
            />
            {parsed.rows.length > 0 && (
              <>
                <DataTable
                  label="The accounts to add"
                  columns={CHART_COLUMNS}
                  rows={parsed.rows.slice(0, 12).map((r) => ({ ...r, id: r.code }))}
                  empty="No accounts yet."
                />
                {parsed.rows.length > 12 && (
                  <p className="bp-setup-small">and {parsed.rows.length - 12} more</p>
                )}
              </>
            )}
          </div>
        )}
        <Send busy={busy} busyLabel="Adding them…" error={error} ready={ready}>
          Add the accounts
        </Send>
      </form>
      {done && <Notice tone="done" title={done} />}
    </Section>
  );
}

function OpeningBalances({ toast }: { toast: Toast }) {
  const accounts = useQuery<{ accounts: Account[] }>('accounts.list', { includeInactive: false });
  const banks = (accounts.data?.accounts ?? []).filter((a) => a.subtype === 'bank');
  const [asOf, setAsOf] = useState('');
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState<string | undefined>();
  const [ar, setAr] = useState('');
  const [ap, setAp] = useState('');
  const [deferred, setDeferred] = useState('');
  const [statements, setStatements] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    tieOut: TieOut;
    entry: { entryId: string; number: string; postingDate: string } | null;
    ok: boolean;
  } | null>(null);
  const parsed = trialRows(csv);
  const diff = parsed.debitCents - parsed.creditCents;
  const figures = [ar, ap, deferred, ...banks.map((b) => statements[b.code] ?? '')];
  const missing = [
    ...(!asOf ? ['the switch-over date'] : []),
    ...(!parsed.rows.length ? ['the trial balance'] : []),
    ...(tieCents(ar) === null ? ['the receivables aging total'] : []),
    ...(tieCents(ap) === null ? ['the payables aging total'] : []),
    ...(tieCents(deferred) === null ? ['the unused memberships (deferred revenue)'] : []),
    ...banks
      .filter((b) => tieCents(statements[b.code] ?? '') === null)
      .map((b) => `${b.code} ${b.name}’s statement balance`),
  ];
  const ready = !missing.length && !parsed.problems.length && diff === 0 && figures.every((f) => f.trim());
  const send = async () => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await client.call<{
        tieOut: TieOut;
        entry: { entryId: string; number: string; postingDate: string } | null;
      }>('imports.openingBalances', {
        asOf,
        rows: parsed.rows,
        tieOut: {
          arAgingCents: tieCents(ar)!,
          apAgingCents: tieCents(ap)!,
          deferredRevenueCents: tieCents(deferred)!,
          bankStatements: banks.map((b) => ({
            accountCode: b.code,
            statementCents: tieCents(statements[b.code]!)!,
          })),
        },
        ...(fileName ? { fileName } : {}),
      });
      setResult({ tieOut: r.tieOut, entry: r.entry, ok: true });
      toast(
        r.entry
          ? `Posted ${r.entry.number} on ${dayText(r.entry.postingDate)}.`
          : 'Nothing changed: these balances were already in at this date.',
      );
    } catch (e) {
      // A failed tie-out says which checks, and its table comes with it.
      const tie = e instanceof RpcError ? (e.detail as { tieOut?: TieOut } | null)?.tieOut : undefined;
      if (tie) setResult({ tieOut: tie, entry: null, ok: false });
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const figure = (label: string, value: string, set: (v: string) => void) => (
    <TextField label={label} inputMode="decimal" value={value} onChange={set} />
  );
  return (
    <Section title="Opening balances" level={3}>
      <p className="bp-setup-say">
        The old books’ trial balance at the switch-over date. It posts only once it balances and ties out to
        the old aging reports, the unused memberships and each bank statement. Importing again for the same
        date (the accountant’s adjusted file, say) posts only the differences.
      </p>
      <form
        className="bp-setup-form"
        aria-label="Post the opening balances"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy) void send();
        }}
      >
        <div className="bp-setup-wide bp-setup-fit">
          <TextField label="Switch-over date" type="date" width="date" value={asOf} onChange={setAsOf} />
        </div>
        <CsvBox
          label="Trial balance (account code, debit, credit)"
          hint="One account a line, in dollars: 1000, 12500.00, then a blank credit. The codes must be in your chart: bring the old chart in above first."
          value={csv}
          onChange={(t, name) => {
            setCsv(t);
            if (name) setFileName(name);
            setResult(null);
          }}
        />
        {csv.trim() && (
          <div className="bp-setup-wide">
            <Read
              title={
                `${parsed.rows.length} ${parsed.rows.length === 1 ? 'account' : 'accounts'} · debits ` +
                `${money(parsed.debitCents)} · credits ${money(parsed.creditCents)} · ` +
                (diff ? `off by ${money(Math.abs(diff))}: it must balance` : 'balances')
              }
              problems={parsed.problems}
            />
          </div>
        )}
        <h4 className="bp-setup-wide bp-setup-sub">The tie-out (all required)</h4>
        {figure('Receivables aging total', ar, setAr)}
        {figure('Payables aging total', ap, setAp)}
        {figure('Unused memberships (deferred revenue)', deferred, setDeferred)}
        {banks.map((b) => (
          <TextField
            key={b.id}
            label={`${b.code} ${b.name}: statement balance`}
            inputMode="decimal"
            value={statements[b.code] ?? ''}
            onChange={(v) => setStatements((s) => ({ ...s, [b.code]: v }))}
          />
        ))}
        {missing.length > 0 && (
          <p className="bp-setup-wide bp-setup-small">Still needed: {missing.join(', ')}.</p>
        )}
        <Send busy={busy} busyLabel="Checking and posting…" error={error} ready={ready}>
          Check and post the opening balances
        </Send>
      </form>
      {result && (
        <div className="bp-setup-read">
          <h4 className="bp-setup-sub">{result.ok ? 'It tied out' : 'It didn’t tie out: nothing posted'}</h4>
          <DataTable
            label="The tie-out"
            columns={TIE_COLUMNS}
            rows={result.tieOut.checks.map((c) => ({ ...c, id: c.what }))}
            empty="No checks came back."
          />
          {result.ok && (
            <Notice
              tone="done"
              title={
                result.entry ? (
                  <>
                    Posted{' '}
                    <Link href={docHref({ kind: 'journal-entry', id: result.entry.entryId })}>
                      {result.entry.number}
                    </Link>{' '}
                    on {dayText(result.entry.postingDate)}.
                  </>
                ) : (
                  'Nothing changed: these balances were already in at this date.'
                )
              }
            />
          )}
        </div>
      )}
    </Section>
  );
}

function History({ toast }: { toast: Toast }) {
  const units = useQuery<{ businessUnits: { id: string; name: string }[] }>('businessUnits.list', {});
  const [picked, setPicked] = useState<string>(HISTORY_SOURCES[0].code);
  const [other, setOther] = useState('');
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const parsed = historyRows(csv, units.data?.businessUnits ?? []);
  const source = picked === 'other' ? sourceCode(other) : picked;
  const ready = source !== null && parsed.rows.length > 0 && !parsed.problems.length;
  const send = async () => {
    if (!ready || !source) return;
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ months: number; rows: number }>('imports.historySummaries', {
        source,
        rows: parsed.rows,
      });
      const said = `Kept ${r.rows} ${r.rows === 1 ? 'figure' : 'figures'} for ${r.months} ${r.months === 1 ? 'month' : 'months'}. Reports show them beside this year’s.`;
      setDone(said);
      toast(said);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title="History summaries" level={3}>
      <p className="bp-setup-say">
        Monthly totals from the old system, kept so reports can compare this year with last. They are never
        posted to the ledger. Importing a month again replaces its figures.
      </p>
      <form
        className="bp-setup-form"
        aria-label="Keep the old history"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy) void send();
        }}
      >
        <Source picked={picked} other={other} onPick={setPicked} onOther={setOther} />
        <CsvBox
          label="Totals (month, figure, amount, department)"
          hint={`One figure a line: 2025-09, Revenue, 48250.00, Drains. The figures: ${HISTORY_METRICS.map((m) => m.label.toLowerCase()).join(', ')}. The department is optional.`}
          value={csv}
          onChange={(t) => {
            setCsv(t);
            setDone(null);
          }}
        />
        {csv.trim() && (
          <div className="bp-setup-wide">
            <Read
              title={`${parsed.rows.length} ${parsed.rows.length === 1 ? 'figure' : 'figures'} ready`}
              problems={parsed.problems}
            />
          </div>
        )}
        <Send busy={busy} busyLabel="Keeping them…" error={error} ready={ready}>
          Keep the history
        </Send>
      </form>
      {done && <Notice tone="done" title={done} />}
    </Section>
  );
}
